import { colorizeCrop, dayWeight, solarZenithDeg } from "./goesColor.js";
import { cropStride, indexToLatLon } from "./goesGrid.js";

// GeoColor at native resolution. MCMIP resamples every band to 2 km, so the
// visible bands come from their single-band CMIP files instead: band 2 (red,
// 0.5 km) sets the output grid, bands 1 and 3 (1 km) and band 13 (2 km, from
// MCMIP) are upsampled onto it.
export const GEO_HIRES_BANDS = Object.freeze([1, 2, 3]);
export const GEO_BASE_BAND = 2;
// Upsampled bands borrow band 2's detail, as in the Geo2Grid/satpy true color:
// each is scaled by band 2 over band 2 smoothed to that band's resolution.
export const SHARPEN_MAX_RATIO = 1.5;

// How many `fine` cells make one `coarse` cell, when the two grids nest edge
// to edge (same sector and scan). Null otherwise.
export function gridRatio(coarse, fine) {
  if (!coarse || !fine || !(fine.xAngleStep) || !(fine.yAngleStep)) return null;
  const rx = coarse.xAngleStep / fine.xAngleStep;
  const ry = coarse.yAngleStep / fine.yAngleStep;
  const r = Math.round(rx);
  if (!(r >= 1) || Math.abs(rx - r) > 1e-3 || Math.abs(ry - r) > 1e-3) return null;
  const ox = (coarse.xAngle0 - fine.xAngle0) / fine.xAngleStep;
  const oy = (coarse.yAngle0 - fine.yAngle0) / fine.yAngleStep;
  if (Math.abs(ox) > 0.01 || Math.abs(oy) > 0.01) return null;
  if (coarse.nx * r !== fine.nx || coarse.ny * r !== fine.ny) return null;
  return r;
}

// Band 2's grid as it nests in a 2 km grid, so a crop can be planned before
// any band file is open. The band reads check it against the real one.
export function fineProjFor(baseProj, factor = 4) {
  return {
    ...baseProj,
    xAngleStep: baseProj.xAngleStep / factor,
    yAngleStep: baseProj.yAngleStep / factor,
    nx: baseProj.nx * factor,
    ny: baseProj.ny * factor,
  };
}

// Whether any of the crop is in daylight. Night GeoColor is band 13 alone, so
// the visible bands are not worth fetching.
export function cropSeesDay(window, proj, timeMs) {
  if (!Number.isFinite(timeMs)) return true;
  const xs = [window.x0, (window.x0 + window.x1) / 2, window.x1 - 1];
  const ys = [window.y0, (window.y0 + window.y1) / 2, window.y1 - 1];
  for (const y of ys) {
    for (const x of xs) {
      const ll = indexToLatLon(x, y, proj);
      if (!ll || dayWeight(solarZenithDeg(ll.lat, ll.lon, timeMs)) > 0) return true;
    }
  }
  return false;
}

// The output grid for a crop of the 2 km `window`: band 2's own grid,
// decimated to fit `maxSize` cells a side. Null when that is no finer than the
// 2 km crop already is.
export function hiresPlan(window, baseProj, fineProj, maxSize) {
  const factor = gridRatio(baseProj, fineProj);
  if (!factor || factor < 2) return null;
  const baseStride = window.stride || 1;
  const span = Math.max(window.x1 - window.x0, window.y1 - window.y0) * factor;
  const stride = cropStride(span, maxSize);
  if (stride >= baseStride * factor) return null;
  const x0 = window.x0 * factor;
  const x1 = window.x1 * factor;
  const y0 = window.y0 * factor;
  const y1 = window.y1 * factor;
  return {
    factor,
    window: { x0, x1, y0, y1, stride },
    width: Math.ceil((x1 - x0) / stride),
    height: Math.ceil((y1 - y0) / stride),
  };
}

// The read window for a band whose cells are `ratio` output-grid cells wide.
// A band no finer than the output is read at the matching stride; a coarser
// one is read whole and upsampled.
export function bandWindow(plan, ratio) {
  const { x0, x1, y0, y1, stride } = plan.window;
  return {
    x0: x0 / ratio,
    x1: x1 / ratio,
    y0: y0 / ratio,
    y1: y1 / ratio,
    stride: Math.max(1, stride / ratio),
  };
}

// Output cells per band sample along each axis.
export function upsampleFactor(plan, ratio) {
  const bandStride = Math.max(1, plan.window.stride / ratio);
  return (ratio * bandStride) / plan.window.stride;
}

function finiteAt(src, index) {
  const value = src[index];
  return Number.isFinite(value) ? value : NaN;
}

// Places a band read with bandWindow(plan, ratio) onto the output grid.
// Output cell c is centered on band-2 cell x0 + c*stride (as the map draws a
// decimated crop); band sample k starts at band-2 cell x0 + k*ratio*bandStride
// and is `ratio` cells wide. Bilinear between samples; a missing one drops out.
export function resampleBand(src, srcWidth, srcHeight, plan, ratio) {
  const { width, height } = plan;
  const up = upsampleFactor(plan, ratio);
  if (up === 1 && srcWidth === width && srcHeight === height) return src;
  const out = new Float32Array(width * height);
  if (up === 1) {
    for (let row = 0; row < height; row++) {
      const srow = Math.min(srcHeight - 1, row) * srcWidth;
      for (let col = 0; col < width; col++) {
        out[(row * width) + col] = finiteAt(src, srow + Math.min(srcWidth - 1, col));
      }
    }
    return out;
  }
  const bandStride = Math.max(1, plan.window.stride / ratio);
  const shift = (ratio - 1) / (2 * ratio * bandStride);
  const colIndex = new Int32Array(width);
  const colFrac = new Float32Array(width);
  for (let col = 0; col < width; col++) {
    const k = Math.max(0, Math.min(srcWidth - 1, (col / up) - shift));
    colIndex[col] = Math.min(srcWidth - 2, Math.floor(k));
    colFrac[col] = srcWidth > 1 ? k - colIndex[col] : 0;
    if (srcWidth === 1) colIndex[col] = 0;
  }
  for (let row = 0; row < height; row++) {
    const k = Math.max(0, Math.min(srcHeight - 1, (row / up) - shift));
    const r0 = srcHeight > 1 ? Math.min(srcHeight - 2, Math.floor(k)) : 0;
    const fy = srcHeight > 1 ? k - r0 : 0;
    const r1 = srcHeight > 1 ? r0 + 1 : 0;
    for (let col = 0; col < width; col++) {
      const c0 = colIndex[col];
      const c1 = srcWidth > 1 ? c0 + 1 : 0;
      const fx = colFrac[col];
      const v00 = src[(r0 * srcWidth) + c0];
      const v01 = src[(r0 * srcWidth) + c1];
      const v10 = src[(r1 * srcWidth) + c0];
      const v11 = src[(r1 * srcWidth) + c1];
      const w00 = Number.isFinite(v00) ? (1 - fx) * (1 - fy) : 0;
      const w01 = Number.isFinite(v01) ? fx * (1 - fy) : 0;
      const w10 = Number.isFinite(v10) ? (1 - fx) * fy : 0;
      const w11 = Number.isFinite(v11) ? fx * fy : 0;
      const weight = w00 + w01 + w10 + w11;
      out[(row * width) + col] = weight > 0
        ? ((w00 ? v00 * w00 : 0) + (w01 ? v01 * w01 : 0) + (w10 ? v10 * w10 : 0) + (w11 ? v11 * w11 : 0)) / weight
        : NaN;
    }
  }
  return out;
}

// Band 2 averaged over each `up` x `up` block, the footprint of one sample of
// a band upsampled by `up`.
export function blockMean(src, width, height, up) {
  const bw = Math.ceil(width / up);
  const bh = Math.ceil(height / up);
  const out = new Float32Array(bw * bh);
  for (let by = 0; by < bh; by++) {
    for (let bx = 0; bx < bw; bx++) {
      let sum = 0;
      let count = 0;
      for (let y = by * up; y < Math.min(height, (by + 1) * up); y++) {
        for (let x = bx * up; x < Math.min(width, (bx + 1) * up); x++) {
          const value = src[(y * width) + x];
          if (Number.isFinite(value)) {
            sum += value;
            count += 1;
          }
        }
      }
      out[(by * bw) + bx] = count ? sum / count : NaN;
    }
  }
  return { grid: out, width: bw, height: bh };
}

// Band 2 over band 2 smoothed to an upsampled band's resolution, clamped as
// satpy does. Multiplying an upsampled band by it restores the fine edges.
export function sharpenRatio(base, plan, ratio) {
  const up = upsampleFactor(plan, ratio);
  if (up === 1) return null;
  const { width, height } = plan;
  const coarse = blockMean(base, width, height, up);
  const smooth = resampleBand(coarse.grid, coarse.width, coarse.height, plan, ratio);
  const out = new Float32Array(width * height);
  for (let i = 0; i < out.length; i++) {
    const value = base[i] / smooth[i];
    out[i] = Number.isFinite(value) && value >= 0 ? Math.min(SHARPEN_MAX_RATIO, value) : 1;
  }
  return out;
}

export function applyRatio(grid, ratio) {
  if (!ratio) return grid;
  const out = new Float32Array(grid.length);
  for (let i = 0; i < grid.length; i++) out[i] = grid[i] * ratio[i];
  return out;
}

// The CMIP object holding one band of the scan in an MCMIP object, as a list
// prefix (the end and creation stamps differ between the two).
export function cmipPrefixFor(mcmipName, band) {
  const match = /^ABI-L2-MCMIP([CFM])\/(\d{4}\/\d{3}\/\d{2})\/OR_ABI-L2-MCMIP([CFM]\d?)-M(\d+)_(G\d+)_(s\d+)_/
    .exec(String(mcmipName || ""));
  if (!match) return null;
  const [, dir, hour, sector, mode, sat, start] = match;
  const bb = String(band).padStart(2, "0");
  return `ABI-L2-CMIP${dir}/${hour}/OR_ABI-L2-CMIP${sector}-M${mode}C${bb}_${sat}_${start}_`;
}

// The read for one band of a planned crop: which file, how coarse it is next
// to band 2, and its window.
export function hiresReads(plan, files, mcmip) {
  const ratios = { 1: 2, 2: 1, 3: 2, 13: plan.factor };
  const byBand = new Map(files.map((file) => [file.band, file]));
  // Band 2 is the biggest read, so it goes first.
  return [2, 1, 3, 13].map((band) => {
    const file = band === 13 ? mcmip : byBand.get(band);
    return file ? { band, url: file.url, size: file.size, ratio: ratios[band], window: bandWindow(plan, ratios[band]) } : null;
  });
}

// Upsamples, sharpens and colors the band reads. `reads` hold each band's
// grid, its size and the projection of the file it came from. Null when the
// files' grids don't nest the way the plan assumed.
export function composeHires({ plan, reads, product, timeMs }) {
  const fine = reads.find((read) => read.band === GEO_BASE_BAND)?.proj;
  if (!fine) return null;
  const grids = {};
  for (const read of reads) {
    if (gridRatio(read.proj, fine) !== read.ratio) return null;
    grids[read.band] = resampleBand(read.grid, read.width, read.height, plan, read.ratio);
  }
  for (const read of reads) {
    if (read.band === GEO_BASE_BAND || read.band === 13) continue;
    grids[read.band] = applyRatio(grids[read.band], sharpenRatio(grids[GEO_BASE_BAND], plan, read.ratio));
  }
  const { window, width, height } = plan;
  const rgba = colorizeCrop({
    product,
    grids,
    width,
    height,
    y0: window.y0,
    x0: window.x0,
    stride: window.stride,
    proj: fine,
    timeMs,
  });
  return { rgba, proj: fine };
}
